import { gql, useMutation, useQuery } from '@apollo/client';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/solid';
import { error } from 'console';
import React, { useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';
import getModalContext from '../../../../context/getModalContext';
import useAuth from '../../../../hooks/useAuth';
import { ListingDetails_ListingData_listing } from '../../../../types/graph/ListingDetails_ListingData';
import { Switch } from '@headlessui/react';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

interface IProps {
  listing: ListingDetails_ListingData_listing;
}

const Domain: React.FC<IProps> = ({ listing }) => {
  const [domainName, setDomainName] = useState<string>('');
  const [tld, setTld] = useState<string>('.com');
  const [available, setAvailable] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [checked, setChecked] = useState<boolean>(false);
  const [providerError, setProviderError] = useState<boolean>(false);
  const { account } = useAuth();
  const { pathname } = useLocation();

  const {
    data: productsData,
    loading: productsLoading,
    error: productsError,
  } = useQuery(GET_PRODUCTS);

  const [checkDomainAvailability] = useMutation(CHECK_DOMAIN_AVAILABILITY, {
    onCompleted: (response) => {
      console.log('response: ', response);
      if (response?.checkDomainAvailability?.available) {
        setAvailable(true);
      } else {
        setAvailable(false);
      }
      setChecked(true);
      setLoading(false);
    },
  });

  const handleCheckAvailability = () => {
    if (domainName && domainName !== '') {
      setLoading(true);
      try {
        checkDomainAvailability({
          variables: {
            domain: domainName + tld,
          },
        });
      } catch (error) {
        setProviderError(true);
      }
    }
  };

  const [createCheckoutSession] = useMutation(CREATE_CHECKOUT_SESSION, {
    onCompleted: (data) => {
      //redirect
      console.log(data);
      window.location.replace(data.createCheckoutSession.url);
    },
  });

  const handleDomainPurchase = () => {
    const domainProduct = productsData.products.filter(
      (item: any) => item.productId === 'domain'
    )[0];

    console.log('DOMAIN PRODUCT', domainProduct);

    createCheckoutSession({
      variables: {
        accountId: account!.id,
        input: { items: [{ ...domainProduct, details: domainName + tld }] },
        redirect: pathname,
        listingId: listing.id,
      },
    });
  };

  return (
    <div className="md:grid md:grid-cols-3 md:gap-6">
      <div className="md:col-span-1">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Custom Domain
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Register a custom domain for your listing tour website.
        </p>
      </div>
      <div className="mt-5 md:mt-0 md:col-span-2 pb-10">
        <form className="space-y-6" action="#" method="POST">
          <div className="grid grid-cols-9 gap-4">
            <div className="col-span-4 sm:col-span-4">
              <label
                htmlFor="domainName"
                className="block text-sm font-medium text-gray-700"
              >
                Domain name
              </label>
              <input
                type="text"
                name="domainName"
                id="domainName"
                value={domainName}
                onChange={(e) => {
                  setChecked(false);
                  setAvailable(false);
                  setDomainName(
                    e.target.value.replace('.com', '').replace('.ca', '')
                  );
                }}
                className="mt-1 focus:ring-light-blue-500 focus:border-light-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
            </div>
            <div className="col-span-2 sm:col-span-2">
              <label
                htmlFor="country"
                className="block text-sm font-medium text-gray-700"
              >
                TLD
              </label>
              <select
                id="tld"
                name="tld"
                value={tld}
                onChange={(e) => setTld(e.target.value)}
                className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm"
              >
                <option value=".com">.com ($19.00)</option>
              </select>
            </div>
            <div className="col-span-2 sm:col-span-2 flex items-end">
              {available ? (
                <button
                  type="button"
                  className="bg-primary border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light"
                  onClick={handleDomainPurchase}
                >
                  <span>Purchase Domain</span>
                </button>
              ) : (
                <button
                  type="button"
                  disabled={loading}
                  className={`${
                    loading ? 'cursor-not-allowed' : ''
                  } bg-white inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light`}
                  onClick={handleCheckAvailability}
                >
                  {loading && (
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx={12}
                        cy={12}
                        r={10}
                        stroke="currentColor"
                        strokeWidth={4}
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  )}
                  {loading ? (
                    <span>Checking...</span>
                  ) : (
                    <span>Check Availability</span>
                  )}
                </button>
              )}
            </div>
            <div className="col-span-5">
              {checked &&
                !providerError &&
                (available ? (
                  <div className="rounded-md bg-green-50 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <CheckCircleIcon
                          className="h-5 w-5 text-green-400"
                          aria-hidden="true"
                        />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800">
                          Domain is available for purchase
                        </h3>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-md bg-red-50 p-4">
                    <div className="flex">
                      <div className="flex-grow-0">
                        <XCircleIcon
                          className="h-5 w-5 text-red-400"
                          aria-hidden="true"
                        />
                      </div>
                      <div className="ml-3 flex-grow-0">
                        <h3 className="text-sm font-medium text-red-800">
                          Domain is not available, try a different variation
                        </h3>
                      </div>
                    </div>
                  </div>
                ))}
              {providerError && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <div className="flex-grow-0">
                      <XCircleIcon
                        className="h-5 w-5 text-red-400"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="ml-3 flex-grow-0">
                      <h3 className="text-sm font-medium text-red-800">
                        Provider error, contact support
                      </h3>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="mt-2 col-span-7 sm:col-span-7">
              <ul className="divide-y divide-gray-200">
                {listing.website?.domains &&
                  listing.website.domains.map((domain) => (
                    <li className="py-4 flex">
                      <div className="w-full flex items-center justify-between">
                        <a
                          target="_blank"
                          rel="noreferrer"
                          href={`http://${domain!.name}`}
                          className="flex flex-grow text-sm font-medium text-primary"
                        >
                          {domain!.name}
                        </a>
                        {/* <Switch
                          checked={domain!.enabled || true}
                          onChange={() => {}}
                          className={classNames(
                            domain!.enabled ? 'bg-primary' : 'bg-gray-200',
                            'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light'
                          )}
                        >
                          <span className="sr-only">Domain enabled</span>
                          <span
                            aria-hidden="true"
                            className={classNames(
                              domain!.enabled
                                ? 'translate-x-5'
                                : 'translate-x-0',
                              'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                            )}
                          />
                        </Switch> */}
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Domain;

const CHECK_DOMAIN_AVAILABILITY = gql`
  mutation ListingsDetailsWebsite_CheckDomainAvailability($domain: String!) {
    checkDomainAvailability(domain: $domain) {
      domainName
      status
      available
    }
  }
`;

const GET_PRODUCTS = gql`
  query ListingDetailsWebsite_getProductsData {
    products {
      productId
      priceId
      type
      recurrence
      name
      description
      details
      features
      amount
      active
    }
  }
`;

const CREATE_CHECKOUT_SESSION = gql`
  mutation ListingDetailsWebsite_createCheckoutSession(
    $accountId: String!
    $input: CartInput!
    $redirect: String!
    $listingId: String
  ) {
    createCheckoutSession(
      accountId: $accountId
      input: $input
      redirect: $redirect
      listingId: $listingId
    ) {
      url
    }
  }
`;
